import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Bold, Italic, List, ListOrdered, Info, AlertCircle, Clock, Calendar as CalendarDaysIcon } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Tables, Database } from '@/integrations/supabase/types';
import { Staff } from './SchedulerTypes';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { formatDuration } from '@/lib/timeUtils';
import { validateTaskForAllocation, getAllocationPreview, getWorkingDaysBetween, HOURS_PER_WORKING_DAY } from '@/lib/taskTimeUtils';

type StaffTodo = Tables<'staff_todos'> & { category?: string; project_id?: string };
type Project = Tables<'projects'>;

interface TaskTodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (todo: Omit<StaffTodo, 'id' | 'created_at' | 'updated_at'>) => void;
  staff: Staff[];
  todo?: StaffTodo | null;
  defaultStaffId?: string;
  projects?: Project[];
  defaultProjectId?: string;
}

const TASK_CATEGORIES = [
  'General Admin',
  'Marketing',
  'HR & Staff Management',
  'Payroll – Staff',
  'Payroll – Doctor',
  'Accounts',
  'Finance',
  'Operations',
  'Compliance',
  'Events – Internal',
  'Events – Education',
  'Events – External',
  'Management Planning',
  'Management Reports'
];

const TaskTodoModal: React.FC<TaskTodoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  staff,
  todo,
  defaultStaffId,
  projects = [],
  defaultProjectId,
}) => {
  const { user, canEditTaskDeadline } = useAuth();
  // When creating a new task, everyone can set the deadline
  // When editing an existing task, only certain roles can change the deadline
  const canEditDeadline = !todo || canEditTaskDeadline();
  const isAdmin = user?.role === 'admin';
  // All users can edit descriptions
  const canEditDescription = true;
  
  const [staffId, setStaffId] = useState(defaultStaffId || '');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [estimatedHours, setEstimatedHours] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [deadline, setDeadline] = useState<Date | undefined>(undefined);
  const [completedAt, setCompletedAt] = useState<Date | undefined>(undefined);
  const [status, setStatus] = useState<'pending' | 'in-progress' | 'review' | 'on-hold' | 'completed' | 'cancelled'>('pending');
  const [category, setCategory] = useState<string>('');
  const [taskType, setTaskType] = useState<'Routine' | 'Ad-hoc'>('Ad-hoc');
  const [projectId, setProjectId] = useState<string>('none');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [lateReason, setLateReason] = useState<string>('');
  const [showLateReasonPrompt, setShowLateReasonPrompt] = useState(false);

  // Use a ref to track if we've initialized the form for this task
  const initializedTodoId = React.useRef<string | null>(null);

  // Clear validation errors when user fills in required fields
  useEffect(() => {
    if (showValidationErrors && deadline && category) {
      setShowValidationErrors(false);
    }
  }, [deadline, category, showValidationErrors]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'list-disc',
          },
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'list-decimal',
          },
        },
        listItem: {
          HTMLAttributes: {
            class: 'ml-4',
          },
        },
      }),
      Placeholder.configure({
        placeholder: 'Enter task description (optional)',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
    },
    editable: canEditDescription,
  }, [canEditDescription]);

  useEffect(() => {
    // Only initialize once when modal opens
    if (!isOpen) {
      initializedTodoId.current = null;
      return;
    }

    const todoId = todo?.id || 'new';
    if (initializedTodoId.current === todoId) {
      return; // Already initialized for this task
    }

    if (todo) {
      setStaffId(todo.staff_id);
      setTitle(todo.title);
      editor?.commands.setContent(todo.description || '');
      setPriority(todo.priority as 'low' | 'medium' | 'high' | 'urgent');
      // Convert minutes to hours for display
      setEstimatedHours(todo.estimated_duration ? String(todo.estimated_duration / 60) : '');
      setStartDate(todo.start_date ? new Date(todo.start_date) : undefined);
      setDeadline(todo.deadline ? new Date(todo.deadline) : undefined);
      setCompletedAt(todo.completed_at ? new Date(todo.completed_at) : undefined);
      setStatus(todo.status as Database['public']['Enums']['task_status_new']);
      // Convert 'none' to empty string for validation
      const existingCategory = todo.category;
      setCategory(existingCategory === 'none' || !existingCategory ? '' : existingCategory);
      setTaskType((todo.task_type as 'Routine' | 'Ad-hoc') || 'Ad-hoc');
      setProjectId(todo.project_id || 'none');
      setLateReason(todo.late_reason || '');
    } else {
      setStaffId(defaultStaffId || (staff[0]?.id || ''));
      setTitle('');
      editor?.commands.setContent('');
      setPriority('medium');
      setEstimatedHours('');
      setStartDate(undefined);
      setDeadline(undefined);
      setCompletedAt(undefined);
      setStatus('pending');
      setCategory('');
      setTaskType('Ad-hoc');
      setProjectId(defaultProjectId || 'none');
      setLateReason('');
    }
    // Reset validation errors and late reason prompt when modal opens/reopens
    setShowValidationErrors(false);
    setShowLateReasonPrompt(false);

    initializedTodoId.current = todoId;
  }, [todo, staff, defaultStaffId, defaultProjectId, editor, isOpen]);

  // Update editor's editable state when permission changes
  useEffect(() => {
    if (editor && editor.isEditable !== canEditDescription) {
      editor.setEditable(canEditDescription);
    }
  }, [editor, canEditDescription]);

  // Calculate allocation preview and validation
  const allocationInfo = React.useMemo(() => {
    if (!startDate || !deadline || !estimatedHours) {
      return null;
    }

    const estimatedMinutes = parseFloat(estimatedHours) * 60;
    const validation = validateTaskForAllocation(startDate, deadline, estimatedMinutes);
    const preview = getAllocationPreview(startDate, deadline, estimatedMinutes);
    const workingDays = getWorkingDaysBetween(startDate, deadline);

    return {
      isValid: validation.valid,
      message: validation.message,
      preview,
      workingDays,
      totalCapacity: workingDays * HOURS_PER_WORKING_DAY,
    };
  }, [startDate, deadline, estimatedHours]);

  const handleSave = () => {
    // Validate required fields
    if (!staffId || !title.trim() || !deadline || !category) {
      setShowValidationErrors(true);
      return;
    }

    // Check if task is being marked as completed and is late
    const isBeingCompleted = status === 'completed' && todo?.status !== 'completed';
    if (isBeingCompleted && deadline) {
      const deadlineDate = new Date(deadline);
      const completionDate = completedAt || new Date();

      // Add 1 day to deadline to include full deadline day (per migration logic)
      const deadlinePlusOne = new Date(deadlineDate);
      deadlinePlusOne.setDate(deadlinePlusOne.getDate() + 1);
      deadlinePlusOne.setHours(0, 0, 0, 0);

      const completionDateNormalized = new Date(completionDate);
      completionDateNormalized.setHours(0, 0, 0, 0);

      const isLate = completionDateNormalized >= deadlinePlusOne;

      // If late and no late reason provided, show prompt
      if (isLate && !lateReason.trim()) {
        setShowLateReasonPrompt(true);
        return;
      }
    }

    const description = editor?.getHTML() || '';
    const cleanDescription = description === '<p></p>' ? null : description;

    // Convert estimated hours to minutes for storage
    const estimatedMinutes = estimatedHours ? Math.round(parseFloat(estimatedHours) * 60) : null;

    // Check if deadline was extended for an overdue task
    // If so, increment the extension_count
    let newExtensionCount = (todo as any)?.extension_count ?? 0;
    if (todo && todo.deadline && deadline) {
      const oldDeadline = new Date(todo.deadline);
      const newDeadline = new Date(deadline);
      oldDeadline.setHours(0, 0, 0, 0);
      newDeadline.setHours(0, 0, 0, 0);

      // Check if old deadline was overdue
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const wasOverdue = today > oldDeadline;

      // Check if deadline was extended (new deadline is later than old)
      const deadlineExtended = newDeadline > oldDeadline;

      // If task was overdue and deadline was extended, increment extension count
      if (wasOverdue && deadlineExtended) {
        newExtensionCount++;
      }
    }

    onSave({
      staff_id: staffId,
      title: title.trim(),
      description: cleanDescription,
      priority,
      estimated_duration: estimatedMinutes,
      start_date: startDate ? startDate.toISOString() : null,
      deadline: deadline.toISOString(),
      status,
      completed_at: (() => {
        if (status === 'completed') {
          if (completedAt && todo?.completed_at) {
            // User edited the date - preserve original time
            const originalTime = new Date(todo.completed_at);
            const newDate = new Date(completedAt);
            newDate.setHours(originalTime.getHours(), originalTime.getMinutes(), originalTime.getSeconds(), originalTime.getMilliseconds());
            return newDate.toISOString();
          }
          return todo?.completed_at || new Date().toISOString();
        }
        return null;
      })(),
      category: category,
      task_type: taskType,
      project_id: projectId === 'none' ? null : projectId,
      late_reason: lateReason.trim() || null,
      extension_count: newExtensionCount,
    } as Omit<StaffTodo, 'id' | 'created_at' | 'updated_at'>);

    setShowValidationErrors(false);
    setShowLateReasonPrompt(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{todo ? 'Edit Task' : 'Add New Task'}</span>
            {todo?.actual_duration && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(todo.actual_duration)}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted-foreground mb-2">
          Fields marked with <span className="text-red-500">*</span> are required
        </div>
        {todo && (todo.started_at || todo.completed_at || todo.actual_duration) && (
          <Alert className="mb-4">
            <Clock className="h-4 w-4" />
            <AlertDescription className="text-sm space-y-1">
              <div className="font-semibold">Task Duration Tracking</div>
              {todo.started_at && (
                <div>Started: {format(new Date(todo.started_at), 'PPp')}</div>
              )}
              {todo.completed_at && (
                <div>Completed: {format(new Date(todo.completed_at), 'PPp')}</div>
              )}
              {todo.actual_duration && (
                <div className="font-medium">
                  Total Time: {formatDuration(todo.actual_duration)}
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}
        {showValidationErrors && (!deadline || !category) && (
          <Alert className="mb-4" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please fill in all required fields: 
              {!deadline && ' Deadline'}
              {!deadline && !category && ','}
              {!category && ' Category'}
            </AlertDescription>
          </Alert>
        )}
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="staff" className="text-left sm:text-right">
              Staff Member
            </Label>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                {isAdmin && staff.length > 0 && 'category' in staff[0] ? (
                  // Admin view: Group staff by category (enhanced staff data)
                  <>
                    {['Office Staff', 'Forum', 'Ngee Ann'].map(category => {
                      const categoryStaff = staff.filter((s) => s.category === category);
                      if (categoryStaff.length === 0) return null;

                      return (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1 text-xs font-medium text-muted-foreground bg-muted/50">
                            {category}
                          </div>
                          {categoryStaff.map((staffMember) => (
                            <SelectItem key={staffMember.id} value={staffMember.id} className="pl-4">
                              {staffMember.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  // Non-admin view or regular staff data: Simple list
                  staff.map((staffMember) => (
                    <SelectItem key={staffMember.id} value={staffMember.id}>
                      {staffMember.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="title" className="text-left sm:text-right">
              Title
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full sm:col-span-3"
              placeholder="Enter task title"
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:gap-4">
            <Label htmlFor="description" className="text-left sm:text-right mt-3">
              Description {!canEditDescription && todo && '(Read-only)'}
            </Label>
            <div className={`w-full sm:col-span-3 border rounded-md overflow-hidden ${!canEditDescription && todo ? 'opacity-60' : ''}`}>
              {editor && canEditDescription && (
                <div className="border-b p-2 bg-gray-50 flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    onMouseDown={(e) => e.preventDefault()}
                    className="h-8 w-8 p-0"
                    title="Bold"
                  >
                    <Bold className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    onMouseDown={(e) => e.preventDefault()}
                    className="h-8 w-8 p-0"
                    title="Italic"
                  >
                    <Italic className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={editor.isActive('bulletList') ? 'secondary' : 'ghost'}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    onMouseDown={(e) => e.preventDefault()}
                    className="h-8 w-8 p-0"
                    title="Bullet List"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={editor.isActive('orderedList') ? 'secondary' : 'ghost'}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    onMouseDown={(e) => e.preventDefault()}
                    className="h-8 w-8 p-0"
                    title="Numbered List"
                  >
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="min-h-[150px]">
                <EditorContent editor={editor} />
              </div>
              {!canEditDescription && todo && (
                <Alert className="m-2 py-2">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Only Admin can edit task descriptions
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="priority" className="text-left sm:text-right">
              Priority
            </Label>
            <Select value={priority} onValueChange={(value) => setPriority(value as 'low' | 'medium' | 'high' | 'urgent')}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="estimated-hours" className="text-left sm:text-right">
              Estimated Hours
            </Label>
            <Input
              id="estimated-hours"
              type="number"
              min="0.5"
              step="0.5"
              value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value)}
              className="w-full sm:col-span-3"
              placeholder="e.g., 1.5 (optional)"
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="start-date" className="text-left sm:text-right">
              Start Date
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full sm:col-span-3 justify-start text-left font-normal ${!startDate ? 'text-muted-foreground' : ''}`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'PPP') : 'Select start date (optional)'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                  captionLayout="dropdown-buttons"
                  fromYear={2019}
                  toYear={new Date().getFullYear() + 1}
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="deadline" className="text-left sm:text-right">
              Deadline <span className="text-red-500">*</span>
            </Label>
            <div className="w-full sm:col-span-3 space-y-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal ${!canEditDeadline ? 'opacity-50 cursor-not-allowed' : ''} ${!deadline ? 'text-muted-foreground' : ''} ${showValidationErrors && !deadline ? 'border-red-500' : ''}`}
                    disabled={!canEditDeadline}
                    title={!canEditDeadline ? 'Only Admin can edit deadlines' : undefined}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {deadline ? format(deadline, 'PPP') : 'Select deadline (required)'}
                  </Button>
                </PopoverTrigger>
                {canEditDeadline && (
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={deadline}
                      onSelect={setDeadline}
                      initialFocus
                      captionLayout="dropdown-buttons"
                      fromYear={2019}
                      toYear={new Date().getFullYear() + 1}
                      className="rounded-md border"
                    />
                  </PopoverContent>
                )}
              </Popover>
              {!canEditDeadline && (
                <Alert className="py-2">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Only Admin can edit deadlines
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Time Allocation Preview */}
          {allocationInfo && (
            <Alert className={`${allocationInfo.message && !allocationInfo.isValid ? 'border-orange-500 bg-orange-50' : 'border-blue-500 bg-blue-50'}`}>
              <CalendarDaysIcon className="h-4 w-4" />
              <AlertDescription className="text-sm space-y-1">
                <div className="font-semibold">Time Allocation Preview</div>
                {allocationInfo.preview && (
                  <div className="text-blue-700">{allocationInfo.preview}</div>
                )}
                <div className="text-gray-600">
                  {allocationInfo.workingDays} working day{allocationInfo.workingDays !== 1 ? 's' : ''} ({allocationInfo.totalCapacity}h capacity)
                </div>
                {allocationInfo.message && (
                  <div className={`${allocationInfo.isValid ? 'text-orange-700 font-medium' : 'text-red-700 font-medium'}`}>
                    {allocationInfo.message}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="category" className="text-left sm:text-right">
              Category <span className="text-red-500">*</span>
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className={`w-full sm:col-span-3 ${showValidationErrors && !category ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select category (required)" />
              </SelectTrigger>
              <SelectContent>
                {TASK_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="task-type" className="text-left sm:text-right">
              Task Type
            </Label>
            <Select value={taskType} onValueChange={(value) => setTaskType(value as 'Routine' | 'Ad-hoc')}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select task type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Routine">Routine</SelectItem>
                <SelectItem value="Ad-hoc">Ad-hoc</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="project" className="text-left sm:text-right">
              Project
            </Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select project (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="status" className="text-left sm:text-right">
              Status
            </Label>
            <Select value={status} onValueChange={(value) => setStatus(value as Database['public']['Enums']['task_status_new'])}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></div>
                    Pending
                  </span>
                </SelectItem>
                <SelectItem value="in-progress">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                    In Progress
                  </span>
                </SelectItem>
                <SelectItem value="review">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                    Review
                  </span>
                </SelectItem>
                <SelectItem value="on-hold">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mr-2"></div>
                    On Hold
                  </span>
                </SelectItem>
                <SelectItem value="completed">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                    Completed
                  </span>
                </SelectItem>
                <SelectItem value="cancelled">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                    Cancelled
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* End Date - Only shown when task is already completed */}
          {status === 'completed' && todo?.completed_at && (
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
              <Label htmlFor="completed-at" className="text-left sm:text-right">
                End Date
              </Label>
              <div className="w-full sm:col-span-3 space-y-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        !canEditDeadline ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={!canEditDeadline}
                      title={!canEditDeadline ? 'Only Admin can edit end date' : undefined}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {completedAt ? format(completedAt, 'PPP') : 'Set completion date'}
                    </Button>
                  </PopoverTrigger>
                  {canEditDeadline && (
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={completedAt}
                        onSelect={setCompletedAt}
                        initialFocus
                        captionLayout="dropdown-buttons"
                        fromYear={2019}
                        toYear={new Date().getFullYear() + 1}
                        className="rounded-md border"
                      />
                    </PopoverContent>
                  )}
                </Popover>
                {!canEditDeadline && (
                  <Alert className="py-2">
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Only Admin can edit end date
                    </AlertDescription>
                  </Alert>
                )}
                <div className="text-xs text-muted-foreground">
                  Editing this date can affect whether the task is marked as on-time or late.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Late Reason Prompt */}
        {showLateReasonPrompt && (
          <Alert className="border-orange-500 bg-orange-50">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription>
              <div className="space-y-3">
                <div className="font-semibold text-orange-900">This task is being completed late</div>
                <div className="text-sm text-orange-800">
                  Please provide a reason for the late completion before saving.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="late-reason" className="text-orange-900">
                    Reason for Late Completion <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="late-reason"
                    value={lateReason}
                    onChange={(e) => setLateReason(e.target.value)}
                    placeholder="Explain why this task was completed late..."
                    className="border-orange-300 focus:border-orange-500"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs text-muted-foreground text-left sm:text-right">
                    {lateReason.length}/500 characters
                  </div>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!staffId || !title.trim() || !deadline || !category || (showLateReasonPrompt && !lateReason.trim())}
            title={
              !staffId || !title.trim() || !deadline || !category
                ? 'Please fill in all required fields'
                : showLateReasonPrompt && !lateReason.trim()
                ? 'Please provide a reason for late completion'
                : ''
            }
          >
            {todo ? 'Update' : 'Add'} Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskTodoModal;